import { Component, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ProductService } from '../../services/product.service';
import { Product } from '../../models/product.model';

@Component({
  selector: 'app-product-detail',
  imports: [CommonModule, RouterLink],
  templateUrl: './product-detail.component.html',
  styleUrl: './product-detail.component.scss'
})
export class ProductDetailComponent implements OnInit {
  protected product = signal<Product | undefined>(undefined);
  protected isLoading = signal(true);
  protected selectedImageIndex = signal(0);
  protected showImageModal = signal(false);
  protected modalImageIndex = signal(0);
  protected selectedPaymentOption = signal<number | null>(null);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private productService: ProductService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      const codigo = params['codigo'];
      if (!codigo) {
        this.router.navigate(['/produtos']);
        return;
      }
      this.loadProduct(codigo);
    });
  }

  loadProduct(codigo: string): void {
    this.isLoading.set(true);
    
    this.productService.getProductByCodigo(codigo).subscribe(product => {
      if (!product) {
        this.router.navigate(['/produtos']);
        return;
      }
      this.product.set(product);
      this.isLoading.set(false);
    });
  }

  get images(): string[] {
    const prod = this.product();
    if (!prod) return [];
    return prod.imagens && prod.imagens.length > 0 
      ? prod.imagens 
      : [prod.imagemURL];
  }

  get currentImage(): string {
    return this.images[this.selectedImageIndex()];
  }

  selectImage(index: number): void {
    this.selectedImageIndex.set(index);
  }

  openImageModal(index: number): void {
    this.modalImageIndex.set(index);
    this.showImageModal.set(true);
    document.body.style.overflow = 'hidden';
  }

  closeImageModal(): void {
    this.showImageModal.set(false);
    document.body.style.overflow = 'auto';
  }

  nextModalImage(): void {
    const images = this.images;
    if (this.modalImageIndex() < images.length - 1) {
      this.modalImageIndex.update(i => i + 1);
    }
  }

  prevModalImage(): void {
    if (this.modalImageIndex() > 0) {
      this.modalImageIndex.update(i => i - 1);
    }
  }

  selectPaymentOption(option: number | null): void {
    this.selectedPaymentOption.set(option);
  }

  calculateInstallment(price: number, installments: number): number {
    return price / installments;
  }

  get isInStock(): boolean {
    const prod = this.product();
    return prod ? this.productService.isInStock(prod) : false;
  }

  get hasLowStock(): boolean {
    const prod = this.product();
    return prod ? this.productService.hasLowStock(prod) : false;
  }

  get stockText(): string {
    if (!this.isInStock) return 'Fora de Estoque';
    if (this.hasLowStock) {
      const prod = this.product();
      return `Últimas ${prod?.estoque.saldo} unidades!`;
    }
    return 'Em Estoque';
  }

  formatPrice(price: number): string {
    return price.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  }

  contactWhatsApp(): void {
    const prod = this.product();
    if (!prod) return;
    
    const message = `Olá! Gostaria de consultar o valor do produto:\n\n${prod.nome}\nCódigo: ${prod.codigo}`;
    const phone = '5511987654321'; // Número da empresa
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  }

  requestQuote(): void {
    const prod = this.product();
    if (!prod) return;

    const selectedPayment = this.selectedPaymentOption();
    let paymentInfo = '';
    
    if (selectedPayment === null) {
      paymentInfo = 'Pagamento à vista';
    } else {
      paymentInfo = `Parcelamento em ${selectedPayment}x sem juros`;
    }

    const message = `Olá! Gostaria de solicitar um orçamento para:\n\n${prod.nome}\nCódigo: ${prod.codigo}\nForma de pagamento: ${paymentInfo}`;
    const phone = '5511987654321'; // Número da empresa
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  }
}

